import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Client } from '../http';
import {
  CouponOption, couponOptionLabel, selectableCoupons,
} from '../features/plans/planCoupon';

export interface PlanCouponValue {
  embedded_coupon: string;
  apply_embedded_coupon: boolean;
}

export interface PlanCouponFieldProps {
  embeddedCoupon: string;
  applyEmbeddedCoupon: boolean;
  onChange: (value: PlanCouponValue) => void;
}

/**
 * The plan's own coupon: a switch, and a picker over the coupons table so
 * only codes Chargebee knows can be chosen. Switching off keeps the code,
 * so it can be switched on again without looking it up.
 */
const PlanCouponField = ({ embeddedCoupon, applyEmbeddedCoupon, onChange }: PlanCouponFieldProps) => {
  const [coupons, setCoupons] = useState<CouponOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Client.get<CouponOption[]>('/api/v1/admin/coupons')
      .then((res) => { if (!cancelled) setCoupons(res.data ?? []); })
      .catch((e: unknown) => { if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, []);

  const options = useMemo(() => selectableCoupons(coupons, embeddedCoupon), [coupons, embeddedCoupon]);
  const selected = options.find((c) => c.promo_code === embeddedCoupon) ?? null;

  return (
    <Box>
      <Typography variant="subtitle2">Plan coupon</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Added to every new subscription on this plan, on top of any promo code
        the customer enters at checkout. Switch it off to sell the plan without it.
      </Typography>
      <Stack spacing={1.5}>
        <FormControlLabel
          labelPlacement="start"
          sx={{ justifyContent: 'space-between', ml: 0, mr: 0 }}
          control={(
            <Switch
              checked={applyEmbeddedCoupon}
              onChange={(e) => onChange({ apply_embedded_coupon: e.target.checked, embedded_coupon: embeddedCoupon })}
              inputProps={{ 'aria-label': 'Apply a plan coupon' }}
            />
          )}
          label={<Typography variant="body2">Apply a plan coupon</Typography>}
        />
        <Autocomplete
          options={options}
          value={selected}
          disabled={!applyEmbeddedCoupon}
          getOptionLabel={couponOptionLabel}
          isOptionEqualToValue={(a, b) => a.promo_code === b.promo_code}
          onChange={(_, value) => onChange({ apply_embedded_coupon: applyEmbeddedCoupon, embedded_coupon: value?.promo_code ?? '' })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Coupon"
              helperText={loadError
                ? `Could not load the coupons: ${loadError}`
                : 'Codes come from the coupons table, so only coupons Chargebee knows can be chosen.'}
              error={!!loadError || (applyEmbeddedCoupon && !embeddedCoupon)}
            />
          )}
        />
      </Stack>
    </Box>
  );
};

export default PlanCouponField;
